// @flow

import React, { useEffect, useMemo, useCallback } from 'react';
import flow from 'lodash/fp/flow';
import filter from 'lodash/fp/filter';
import propEq from 'lodash/fp/propEq';
import isEmpty from 'lodash/fp/isEmpty';
import negate from 'lodash/fp/negate';
import head from 'lodash/fp/head';
import prop from 'lodash/fp/prop';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import type { Route } from 'type/type';

import VirtualRace from './VirtualRace/VirtualRace';
import PhysicalRace from './PhysicalRace/PhysicalRace';
import PresentTypeSwitch from './PresentTypeSwitch/PresentTypeSwitch';

import { getEvent, getEventViewType } from '../duck/selector';
import {
  homePageMount as homePageMountAction,
  changeEventViewType as changeEventViewTypeAction,
} from '../duck/duck';
import '../duck/epics';
import './Home.scss';

type Props = {
  routes: Route[],
  viewType: 'PHYSICAL' | 'VIRTUAL',
  participantPresentType: 'HYBRID' | 'VIRTUAL' | 'PHYSICAL',
  homePageMount: Function,
  changeEventViewType: Function,
}

const HYBRID = 'HYBRID';
const VIRTUAL = 'VIRTUAL';
const PHYSICAL = 'PHYSICAL';

export const Home = ({
  routes,
  participantPresentType,
  homePageMount,
  changeEventViewType,
  viewType,
}: Props) => {
  const hasMultiPresentType = useMemo(() => {
    return participantPresentType === HYBRID || (
      participantPresentType === VIRTUAL && flow(
        filter(propEq('virtual', false)),
        negate(isEmpty),
      )(routes)
    );
  }, [ routes, participantPresentType ]);

  const handlePresentTypeChange = useCallback((presentType: 'PHYSICAL' | 'VIRTUAL') => {
    changeEventViewType({ viewType: presentType });
  }, [ changeEventViewType ]);

  const isPrevViewTypeValid = hasMultiPresentType || viewType === participantPresentType;

  const setInitialViewType = useCallback(() => {
    if (!viewType) {
      if (!hasMultiPresentType) {
        changeEventViewType({ viewType: participantPresentType, init: true });
      } else {
        const initialViewType = flow(
          head,
          prop('virtual'),
        )(routes) ? VIRTUAL : PHYSICAL;

        changeEventViewType({ viewType: initialViewType, init: true });
      }
    } else if (!isPrevViewTypeValid) {
      changeEventViewType({ viewType: participantPresentType });
    }
  }, [ viewType, isPrevViewTypeValid, participantPresentType, hasMultiPresentType, routes, changeEventViewType ]);

  useEffect(() => {
    homePageMount();
    setInitialViewType();
  });

  return (
    <div className="page">
      {hasMultiPresentType && (
        <PresentTypeSwitch
          selectedPresentType={viewType}
          onPresentTypeChange={handlePresentTypeChange} />
      )}
      {viewType === VIRTUAL && <VirtualRace />}
      {viewType === PHYSICAL && <PhysicalRace />}
    </div>
  );
};

const mapState = (state) => {
  const event = getEvent(state);

  return event ? {
    viewType: getEventViewType(state),
    routes: event.toJSON().routes,
    participantPresentType: event.toJSON().participantPresentType,
  } : {};
};

const EventHomeContainer = connect(mapState, {
  homePageMount: homePageMountAction,
  changeEventViewType: changeEventViewTypeAction,
})(Home);

export default EventHomeContainer;
